import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Plus, Trash2, Search, Calendar, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import jokes from '../data/jokes';
import { getRandomNouns } from '../data/nouns';
import { getRandomPunchline, PUNCHLINES } from '../data/punchlines';
import { getRandomHeckle, HECKLES } from '../data/heckles';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface PremiseRow {
  premise: string;
  expectation: string;
  opposite: string;
  audioUrl?: string;
}

interface FactsJokeRow {
  fact1: string;
  fact2: string;
  punchline: string;
  audioUrl?: string;
}

interface ExaggerationLadderRow {
  mundane: string;
  slight: string;
  absurd: string;
  audioUrl?: string;
}

interface WhatIfRow {
  scenario: string;
  twist: string;
  outcome: string;
  audioUrl?: string;
}

interface MishearingRow {
  said: string;
  misheard: string;
  punchline: string;
  audioUrl?: string;
}

interface ObjectComplaintRow {
  object: string;
  complaint: string;
  comeback: string;
  audioUrl?: string;
}

interface PersonalFlawFlipRow {
  flaw: string;
  superpower: string;
  backfire: string;
  audioUrl?: string;
}

interface NicknameGeneratorRow {
  action: string;
  nickname: string;
  punchline: string;
  audioUrl?: string;
}

interface WordAssociationRow {
  id: string;
  word1: string;
  word2: string;
  associations1: string[];
  associations2: string[];
  finalJoke: string;
  audioUrl?: string;
}

interface RewritePunchlineRow {
  id: string;
  comedian?: string;
  originalJoke: string;
  originalPunchline: string;
  newPunchline: string;
  audioUrl?: string;
}

interface ReverseSetupRow {
  id: string;
  punchline: string;
  setup: string;
  audioUrl?: string;
}

interface HeckleResponseRow {
  id: string;
  heckle: string;
  response: string;
  audioUrl?: string;
}

const INITIAL_EXERCISES: Exercise[] = [
  {
    id: 'ex1',
    title: 'Premise Reversal',
    description: 'Take a common situation and flip the expected outcome or perspective to create a comedic twist.',
    instructions: [
      'Write down a common, everyday situation in the Premise column',
      'In the Expectation column, write what would normally happen',
      'In the Opposite column, write the reverse of the expectation - this is your punchline!',
      'Try to make the opposite both surprising and logical within its own world'
    ],
    category: 'premise'
  },
  {
    id: 'ex2',
    title: 'Connected Facts Joke',
    description: 'Create humor by finding an unexpected connection between two facts about yourself.',
    instructions: [
      'Write down two true facts about yourself that seem unrelated',
      'Think about possible connections or contrasts between these facts',
      'Create a punchline that cleverly links or contrasts these facts',
      'Make sure the connection is both surprising and makes sense'
    ],
    category: 'premise'
  },
  {
    id: 'ex3',
    title: 'Exaggeration Ladder',
    description: 'Take a mundane detail and escalate it into a ridiculous punchline through progressive exaggeration.',
    instructions: [
      'Write down a mundane thing you did today (e.g., "I drank coffee")',
      'Exaggerate it slightly (e.g., "I drank coffee so strong it woke up my neighbor")',
      'Exaggerate it absurdly (e.g., "I drank coffee so strong it unionized my taste buds and demanded a raise")'
    ],
    category: 'premise'
  },
  {
    id: 'ex4',
    title: 'What If?',
    description: 'Take something familiar and reimagine it with an unexpected twist to create comedic scenarios.',
    instructions: [
      'Choose a real person, place, or thing as your base scenario',
      'Add a "What if?" twist that changes the context completely',
      'Explore the humorous outcomes of this new scenario'
    ],
    category: 'premise'
  },
  {
    id: 'object-complaint',
    title: 'Object Complaint',
    description: 'Give voice to an everyday object and imagine its humorous grievance with you.',
    instructions: [
      'Pick an object you own (e.g., "My laptop")',
      'Write a complaint it might have about you (e.g., "They keep typing with Cheeto fingers all over my keyboard")',
      'Add your witty comeback to its complaint (e.g., "Well, if you weren\'t so tasty to type on...")'
    ],
    category: 'premise'
  },
  {
    id: 'misheard-moment',
    title: 'Misheard Moment',
    description: 'Create humor from misheard phrases and their unexpected interpretations.',
    instructions: [
      'Write something you said or heard today (e.g., "I\'m here to work").',
      'Imagine it was misheard as something else (e.g., "I\'m here to twerk").',
      'Create a punchline from the mix-up (e.g., "The job interview went in a completely different direction").'
    ],
    category: 'premise'
  },
  {
    id: 'personal-flaw-flip',
    title: 'Personal Flaw Flip',
    description: 'Transform your quirks and flaws into comedic superpowers with an ironic twist.',
    instructions: [
      'Pick a flaw or quirk you have (e.g., "I overthink everything")',
      'Imagine it\'s your superpower instead (e.g., "My brain processes a million scenarios per second like a quantum supercomputer")',
      'Write a punchline about how it backfires (e.g., "Spent 3 hours calculating the perfect way to say \'hi\' to my neighbor, ended up with \'good thanks\'")'
    ],
    category: 'premise'
  },
  {
    id: 'nickname-generator',
    title: 'Nickname Generator',
    description: 'Transform everyday traits or habits into clever, culturally-relevant nicknames.',
    instructions: [
      'Write something notable about yourself or a friend (e.g., "Friend loves Basketball and Cookies")',
      'Turn it into a punny nickname (e.g., "Biscotti Pippen")',
      'Create a tagline for your new persona (e.g., "That\'s your 10th Cookie. Relax, Biscotti Pippen")'
    ],
    category: 'premise'
  },
  {
    id: 'word-association',
    title: 'Word Association',
    description: 'Pick two words and explore their associations until you find a comedic connection.',
    instructions: [
      'Enter two unrelated words to start with (e.g., "Coffee" and "Therapy")',
      'List out words you associate with each starting word (Coffee → bitter, morning, jitters, addiction) (Therapy → couch, feelings, expensive, healing)',
      'Look for surprising connections between your associations (expensive + addiction)',
      'Use the connection to craft your joke (e.g., "My therapist says I have a coffee addiction. I told her I spent less on lattes this month than her hourly rate.")'
    ],
    category: 'premise'
  },
  {
    id: 'rewrite-punchline',
    title: 'Rewrite the Punchline',
    description: 'Take an existing joke from a famous comedian, remove the punchline, and write a new one with a different angle.',
    instructions: [
      'Find a joke you like and note the comedian (if known)',
      'Write down the setup part (e.g., "My girlfriend loves to talk on the phone during sex.")',
      'Write down the original punchline (e.g., "The other night she called me from a hotel room.")',
      'Write your new punchline with a different angle (e.g., "I wish she\'d stop putting me on hold.")'
    ],
    category: 'premise'
  },
  {
    id: 'reverse-setup',
    title: 'Reverse Setup',
    description: 'Given a punchline, write a setup that leads to it. Forces you to work backwards and think about misdirection.',
    instructions: [
      'You\'ll be given a punchline (the funny ending)',
      'Your job is to write a setup that makes that punchline land naturally',
      'Think about what assumption or expectation the punchline plays against',
      'The setup should guide the audience down a different path than the punchline reveals'
    ],
    category: 'premise'
  },
  {
    id: 'heckle-responses',
    title: 'Heckle Responses',
    description: 'Practice quick comebacks to heckles. Essential for live performance and thinking on your feet.',
    instructions: [
      'You\'ll get a random heckle (something an audience member might yell)',
      'Write a witty, sharp comeback that puts them in their place without being mean',
      'The best responses are quick, clever, and make the audience laugh with you',
      'Use misdirection, self-deprecation, or turning it back on the heckler'
    ],
    category: 'premise'
  }
];

const INITIAL_EXERCISES_COPY = [...INITIAL_EXERCISES];

const EXAMPLE_ROW: PremiseRow = {
  premise: "I took a self-defense class to feel safer.",
  expectation: "Now I know how to protect myself in a dangerous situation.",
  opposite: "Now I just flinch every time someone reaches for a handshake."
};

const EXAMPLE_FACTS_ROW: FactsJokeRow = {
  fact1: "I'm from Wisconsin",
  fact2: "My family is Persian",
  punchline: "I'm probably the only person who puts cheese on their kebab"
};

const EXAMPLE_EXAGGERATION_ROW: ExaggerationLadderRow = {
  mundane: "I drank coffee",
  slight: "I drank coffee so strong it woke up my neighbor",
  absurd: "I drank coffee so strong it unionized my taste buds and demanded a raise"
};

const EXAMPLE_WHAT_IF_ROW: WhatIfRow = {
  scenario: "Donald Trump",
  twist: "What if he sang 'The 12 Days of Christmas'?",
  outcome: "On the first day of Christmas, my true love gave to me... the most beautiful partridge, folks, absolutely tremendous. Nobody's ever seen a partridge like this before, believe me. And the pear tree? Made in America, fantastic craftsmanship."
};

const EXAMPLE_OBJECT_COMPLAINT_ROW: ObjectComplaintRow = {
  object: "My laptop",
  complaint: "They keep typing with Cheeto fingers all over my keyboard",
  comeback: "Well, if you weren't so tasty to type on..."
};

const EXAMPLE_MISHEARD_ROW: MishearingRow = {
  said: "I'm here to work",
  misheard: "I'm here to twerk",
  punchline: "The job interview went in a completely different direction"
};

const EXAMPLE_PERSONAL_FLAW_FLIP_ROW: PersonalFlawFlipRow = {
  flaw: "I overthink everything",
  superpower: "My brain processes a million scenarios per second like a quantum supercomputer",
  backfire: "Spent 3 hours calculating the perfect way to say 'hi' to my neighbor, ended up with 'good thanks'"
};

const EXAMPLE_NICKNAME_GENERATOR_ROW: NicknameGeneratorRow = {
  action: "Friend loves Basketball and Cookies",
  nickname: "Biscotti Pippen",
  punchline: "That's your 10th Cookie. Relax, Biscotti Pippen"
};

const EXAMPLE_WORD_ASSOCIATION_ROW: WordAssociationRow = {
  id: '1',
  word1: 'Coffee',
  word2: 'Therapy',
  associations1: ['bitter', 'morning', 'jitters', 'addiction'],
  associations2: ['couch', 'feelings', 'expensive', 'healing'],
  finalJoke: 'My therapist says I have a coffee addiction. I told her I spent less on lattes this month than her hourly rate.'
};

const EXAMPLE_REWRITE_PUNCHLINE_ROW: RewritePunchlineRow = {
  id: '1',
  comedian: 'Anthony Jeselnik',
  originalJoke: 'My girlfriend loves to talk on the phone during sex.',
  originalPunchline: 'The other night she called me from a hotel room.',
  newPunchline: ''
};

const EXAMPLE_REVERSE_SETUP_ROW: ReverseSetupRow = {
  id: '1',
  punchline: '...but apparently that\'s not what you do at a funeral.',
  setup: ''
};

const EXAMPLE_HECKLE_RESPONSE_ROW: HeckleResponseRow = {
  id: '1',
  heckle: 'You suck!',
  response: ''
};

const Exercises: React.FC = () => {
  const { user } = useAuth();
  const [selectedExercise, setSelectedExercise] = useState<Exercise>(INITIAL_EXERCISES[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Saved daily-word exercises
  // Saved daily-word exercises — calendar view
  const [allExercises, setAllExercises] = useState<any[]>([]);
  const [loadingSaves, setLoadingSaves] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Calendar state — default to current Eastern Time month
  const todayET = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
  const [calendarYear, setCalendarYear] = useState(() => parseInt(todayET.split('-')[0]));
  const [calendarMonth, setCalendarMonth] = useState(() => parseInt(todayET.split('-')[1])); // 1–12

  // Map date string → exercise for O(1) lookup
  const exerciseByDate = useMemo(() => {
    const map: Record<string, any> = {};
    allExercises.forEach(e => { if (e.date) map[e.date] = e; });
    return map;
  }, [allExercises]);

  // Fetch all daily-word exercises for this user at once
  useEffect(() => {
    if (!user) return;
    const fetchAll = async () => {
      setLoadingSaves(true);
      try {
        const snap = await getDocs(query(collection(db, 'exerciseSaves'), where('userId', '==', user.uid)));
        const results = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter((e: any) => e.type === 'daily-word')
          .sort((a: any, b: any) => (b.date > a.date ? 1 : -1));
        setAllExercises(results);
      } catch (err) {
        console.error('Failed to fetch exercises', err);
      } finally {
        setLoadingSaves(false);
      }
    };
    fetchAll();
  }, [user]);

  // Search filters from already-fetched data — instant, no extra Firestore query
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    return allExercises.filter((e: any) =>
      e.word?.toLowerCase().includes(q) || e.content?.toLowerCase().includes(q)
    );
  }, [searchQuery, allExercises]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'exerciseSaves', id));
      setAllExercises(prev => prev.filter(e => e.id !== id));
      if (selectedDate && exerciseByDate[selectedDate]?.id === id) setSelectedDate(null);
    } catch (err) {
      console.error('Failed to delete', err);
    } finally {
      setDeletingId(null);
    }
  };

  // Calendar navigation helpers
  const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const daysInMonth = new Date(calendarYear, calendarMonth, 0).getDate();
  const firstDayOfWeek = new Date(calendarYear, calendarMonth - 1, 1).getDay();

  const goToPrevMonth = () => {
    setSelectedDate(null);
    if (calendarMonth === 1) { setCalendarMonth(12); setCalendarYear(y => y - 1); }
    else setCalendarMonth(m => m - 1);
  };
  const goToNextMonth = () => {
    setSelectedDate(null);
    if (calendarMonth === 12) { setCalendarMonth(1); setCalendarYear(y => y + 1); }
    else setCalendarMonth(m => m + 1);
  };
  // Swap them so left = prev, right = next
  const handleLeftArrow = goToPrevMonth;
  const handleRightArrow = goToNextMonth;

  const [rows, setRows] = useState<PremiseRow[]>([EXAMPLE_ROW]);
  const [factsRows, setFactsRows] = useState<FactsJokeRow[]>([EXAMPLE_FACTS_ROW]);
  const [exaggerationRows, setExaggerationRows] = useState<ExaggerationLadderRow[]>([EXAMPLE_EXAGGERATION_ROW]);
  const [whatIfRows, setWhatIfRows] = useState<WhatIfRow[]>([EXAMPLE_WHAT_IF_ROW]);
  const [objectComplaintRows, setObjectComplaintRows] = useState<ObjectComplaintRow[]>([EXAMPLE_OBJECT_COMPLAINT_ROW]);
  const [mishearingRows, setMishearingRows] = useState<MishearingRow[]>([EXAMPLE_MISHEARD_ROW]);
  const [personalFlawFlipRows, setPersonalFlawFlipRows] = useState<PersonalFlawFlipRow[]>([EXAMPLE_PERSONAL_FLAW_FLIP_ROW]);
  const [nicknameGeneratorRows, setNicknameGeneratorRows] = useState<NicknameGeneratorRow[]>([EXAMPLE_NICKNAME_GENERATOR_ROW]);
  const [wordAssociationRows, setWordAssociationRows] = useState<WordAssociationRow[]>([]);
  const [rewritePunchlineRows, setRewritePunchlineRows] = useState<RewritePunchlineRow[]>([EXAMPLE_REWRITE_PUNCHLINE_ROW]);
  const [reverseSetupRows, setReverseSetupRows] = useState<ReverseSetupRow[]>([EXAMPLE_REVERSE_SETUP_ROW]);
  const [heckleResponseRows, setHeckleResponseRows] = useState<HeckleResponseRow[]>([EXAMPLE_HECKLE_RESPONSE_ROW]);
  const [newRow, setNewRow] = useState<PremiseRow>({
    premise: '',
    expectation: '',
    opposite: ''
  });
  const [newFactsRow, setNewFactsRow] = useState<FactsJokeRow>({
    fact1: '',
    fact2: '',
    punchline: ''
  });
  const [newExaggerationRow, setNewExaggerationRow] = useState<ExaggerationLadderRow>({
    mundane: '',
    slight: '',
    absurd: ''
  });
  const [newWhatIfRow, setNewWhatIfRow] = useState<WhatIfRow>({
    scenario: '',
    twist: '',
    outcome: ''
  });
  const [newObjectComplaintRow, setNewObjectComplaintRow] = useState<ObjectComplaintRow>({
    object: '',
    complaint: '',
    comeback: ''
  });
  const [newMishearingRow, setNewMishearingRow] = useState<MishearingRow>({
    said: '',
    misheard: '',
    punchline: ''
  });
  const [newPersonalFlawFlipRow, setNewPersonalFlawFlipRow] = useState<PersonalFlawFlipRow>({
    flaw: '',
    superpower: '',
    backfire: ''
  });
  const [newNicknameGeneratorRow, setNewNicknameGeneratorRow] = useState<NicknameGeneratorRow>({
    action: '',
    nickname: '',
    punchline: ''
  });
  const [newWordAssociationRow, setNewWordAssociationRow] = useState<WordAssociationRow>({
    id: '',
    word1: '',
    word2: '',
    associations1: [''],
    associations2: [''],
    finalJoke: ''
  });
  const [newRewritePunchlineRow, setNewRewritePunchlineRow] = useState<RewritePunchlineRow>({
    id: '',
    comedian: '',
    originalJoke: '',
    originalPunchline: '',
    newPunchline: ''
  });

  const handleSave = async () => {
    // Removed unused import and cleaned up the saving functionality
    // You can implement your own saving logic here
  };

  const handleAddRow = () => {
    if (newRow.premise && newRow.expectation && newRow.opposite) {
      setRows([...rows, newRow]);
      setNewRow({ premise: '', expectation: '', opposite: '' });
    }
  };

  const handleAddFactsRow = () => {
    if (newFactsRow.fact1 && newFactsRow.fact2 && newFactsRow.punchline) {
      setFactsRows([...factsRows, newFactsRow]);
      setNewFactsRow({ fact1: '', fact2: '', punchline: '' });
    }
  };

  const handleAddExaggerationRow = () => {
    if (newExaggerationRow.mundane && newExaggerationRow.slight && newExaggerationRow.absurd) {
      setExaggerationRows([...exaggerationRows, newExaggerationRow]);
      setNewExaggerationRow({ mundane: '', slight: '', absurd: '' });
    }
  };

  const handleAddWhatIfRow = () => {
    if (newWhatIfRow.scenario && newWhatIfRow.twist && newWhatIfRow.outcome) {
      setWhatIfRows([...whatIfRows, newWhatIfRow]);
      setNewWhatIfRow({ scenario: '', twist: '', outcome: '' });
    }
  };

  const handleAddObjectComplaintRow = () => {
    if (newObjectComplaintRow.object && newObjectComplaintRow.complaint && newObjectComplaintRow.comeback) {
      setObjectComplaintRows([...objectComplaintRows, newObjectComplaintRow]);
      setNewObjectComplaintRow({ object: '', complaint: '', comeback: '' });
    }
  };

  const handleAddMishearingRow = () => {
    if (newMishearingRow.said && newMishearingRow.misheard && newMishearingRow.punchline) {
      setMishearingRows([...mishearingRows, newMishearingRow]);
      setNewMishearingRow({ said: '', misheard: '', punchline: '' });
    }
  };

  const handleAddPersonalFlawFlipRow = () => {
    if (newPersonalFlawFlipRow.flaw && newPersonalFlawFlipRow.superpower && newPersonalFlawFlipRow.backfire) {
      setPersonalFlawFlipRows([...personalFlawFlipRows, newPersonalFlawFlipRow]);
      setNewPersonalFlawFlipRow({ flaw: '', superpower: '', backfire: '' });
    }
  };

  const handleAddNicknameGeneratorRow = () => {
    if (newNicknameGeneratorRow.action && newNicknameGeneratorRow.nickname && newNicknameGeneratorRow.punchline) {
      setNicknameGeneratorRows([...nicknameGeneratorRows, newNicknameGeneratorRow]);
      setNewNicknameGeneratorRow({ action: '', nickname: '', punchline: '' });
    }
  };

  const handleAddWordAssociationRow = () => {
    const [randomWord1, randomWord2] = getRandomNouns(2);
    const newRow: WordAssociationRow = {
      id: uuidv4(),
      word1: randomWord1,
      word2: randomWord2,
      associations1: [''],
      associations2: [''],
      finalJoke: ''
    };
    setWordAssociationRows([...wordAssociationRows, newRow]);
  };

  const handleAddRewritePunchlineRow = () => {
    const randomJoke = jokes[Math.floor(Math.random() * jokes.length)];
    const newRow: RewritePunchlineRow = {
      id: uuidv4(),
      comedian: randomJoke.Comedian,
      originalJoke: randomJoke.Setup,
      originalPunchline: randomJoke.Punchline,
      newPunchline: ''
    };
    setRewritePunchlineRows([...rewritePunchlineRows, newRow]);
  };

  const handleAddReverseSetupRow = () => {
    const daysSinceEpoch = Math.floor((new Date().getTime() - new Date('2025-01-01T00:00:00').getTime()) / (1000 * 60 * 60 * 24));
    const punchline = getRandomPunchline(daysSinceEpoch + Math.floor(Math.random() * PUNCHLINES.length));
    const newRow: ReverseSetupRow = {
      id: uuidv4(),
      punchline,
      setup: ''
    };
    setReverseSetupRows([...reverseSetupRows, newRow]);
  };

  const handleAddHeckleResponseRow = () => {
    const daysSinceEpoch = Math.floor((new Date().getTime() - new Date('2025-01-01T00:00:00').getTime()) / (1000 * 60 * 60 * 24));
    const heckle = getRandomHeckle(daysSinceEpoch + Math.floor(Math.random() * HECKLES.length));
    const newRow: HeckleResponseRow = {
      id: uuidv4(),
      heckle,
      response: ''
    };
    setHeckleResponseRows([...heckleResponseRows, newRow]);
  };

  const handleDeleteRow = (index: number) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
  };

  const handleDeleteFactsRow = (index: number) => {
    const newRows = factsRows.filter((_, i) => i !== index);
    setFactsRows(newRows);
  };

  const handleDeleteExaggerationRow = (index: number) => {
    const newRows = exaggerationRows.filter((_, i) => i !== index);
    setExaggerationRows(newRows);
  };

  const handleDeleteWhatIfRow = (index: number) => {
    const newRows = whatIfRows.filter((_, i) => i !== index);
    setWhatIfRows(newRows);
  };

  const handleDeleteObjectComplaintRow = (index: number) => {
    const newRows = objectComplaintRows.filter((_, i) => i !== index);
    setObjectComplaintRows(newRows);
  };

  const handleDeleteMishearingRow = (index: number) => {
    const newRows = mishearingRows.filter((_, i) => i !== index);
    setMishearingRows(newRows);
  };

  const handleDeletePersonalFlawFlipRow = (index: number) => {
    const newRows = personalFlawFlipRows.filter((_, i) => i !== index);
    setPersonalFlawFlipRows(newRows);
  };

  const handleDeleteNicknameGeneratorRow = (index: number) => {
    const newRows = nicknameGeneratorRows.filter((_, i) => i !== index);
    setNicknameGeneratorRows(newRows);
  };

  const handleDeleteWordAssociationRow = (index: number) => {
    const newRows = wordAssociationRows.filter((_, i) => i !== index);
    setWordAssociationRows(newRows);
  };

  const handleDeleteRewritePunchlineRow = (index: number) => {
    const newRows = rewritePunchlineRows.filter((_, i) => i !== index);
    setRewritePunchlineRows(newRows);
  };

  const handleDeleteReverseSetupRow = (index: number) => {
    const newRows = reverseSetupRows.filter((_, i) => i !== index);
    setReverseSetupRows(newRows);
  };

  const handleDeleteHeckleResponseRow = (index: number) => {
    const newRows = heckleResponseRows.filter((_, i) => i !== index);
    setHeckleResponseRows(newRows);
  };

  const handleWordAssociationChange = (rowId: string, field: keyof WordAssociationRow, value: string | string[]) => {
    const updatedRows = wordAssociationRows.map(row => {
      if (row.id === rowId) {
        return { ...row, [field]: value };
      }
      return row;
    });
    setWordAssociationRows(updatedRows);
  };

  const handleAddAssociation = (rowId: string, column: 1 | 2) => {
    const updatedRows = wordAssociationRows.map(row => {
      if (row.id === rowId) {
        const field = `associations${column}` as keyof WordAssociationRow;
        return { ...row, [field]: [...(row[field] as string[]), ''] };
      }
      return row;
    });
    setWordAssociationRows(updatedRows);
  };

  const handleRewritePunchlineChange = (rowId: string, field: keyof RewritePunchlineRow, value: string) => {
    const updatedRows = rewritePunchlineRows.map(row => {
      if (row.id === rowId) {
        return { ...row, [field]: value };
      }
      return row;
    });
    setRewritePunchlineRows(updatedRows);
  };

  const renderPremiseTable = () => (
    <div className="space-y-6">
      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Premise</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Expectation</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[43%]">Opposite</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.premise}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.expectation}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[43%]">{row.opposite}</td>
                    <td className="px-4 py-4 align-top w-16">
                      {index !== 0 && (
                        <button
                          onClick={() => handleDeleteRow(index)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                          title="Delete row"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newRow.premise}
                      onChange={(e) => setNewRow({ ...newRow, premise: e.target.value })}
                      placeholder="Enter your premise..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newRow.expectation}
                      onChange={(e) => setNewRow({ ...newRow, expectation: e.target.value })}
                      placeholder="What would normally happen?"
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[43%]">
                    <textarea
                      value={newRow.opposite}
                      onChange={(e) => setNewRow({ ...newRow, opposite: e.target.value })}
                      placeholder="Write your comedic twist..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="w-16"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="sm:hidden space-y-4">
        {rows.map((row, index) => (
          <div key={index} className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Premise</label>
              <p className="text-gray-700 dark:text-gray-300">{row.premise}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Expectation</label>
              <p className="text-gray-700 dark:text-gray-300">{row.expectation}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Opposite</label>
              <p className="text-gray-700 dark:text-gray-300">{row.opposite}</p>
            </div>
            {index !== 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => handleDeleteRow(index)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                  title="Delete row"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Mobile New Row Form */}
        <div className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Premise</label>
            <textarea
              value={newRow.premise}
              onChange={(e) => setNewRow({ ...newRow, premise: e.target.value })}
              placeholder="Enter your premise..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Expectation</label>
            <textarea
              value={newRow.expectation}
              onChange={(e) => setNewRow({ ...newRow, expectation: e.target.value })}
              placeholder="What would normally happen?"
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Opposite</label>
            <textarea
              value={newRow.opposite}
              onChange={(e) => setNewRow({ ...newRow, opposite: e.target.value })}
              placeholder="Write your comedic twist..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={handleAddRow}
          disabled={!newRow.premise || !newRow.expectation || !newRow.opposite}
          className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Row
        </button>
      </div>
    </div>
  );

  const renderFactsJokeTable = () => (
    <div className="space-y-6">
      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Fact 1</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Fact 2</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[43%]">Punchline</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {factsRows.map((row, index) => (
                  <tr key={index}>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.fact1}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.fact2}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[43%]">{row.punchline}</td>
                    <td className="px-4 py-4 align-top w-16">
                      {index !== 0 && (
                        <button
                          onClick={() => handleDeleteFactsRow(index)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                          title="Delete row"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newFactsRow.fact1}
                      onChange={(e) => setNewFactsRow({ ...newFactsRow, fact1: e.target.value })}
                      placeholder="Enter your first fact..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newFactsRow.fact2}
                      onChange={(e) => setNewFactsRow({ ...newFactsRow, fact2: e.target.value })}
                      placeholder="Enter your second fact..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[43%]">
                    <textarea
                      value={newFactsRow.punchline}
                      onChange={(e) => setNewFactsRow({ ...newFactsRow, punchline: e.target.value })}
                      placeholder="Write your punchline..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="w-16"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="sm:hidden space-y-4">
        {factsRows.map((row, index) => (
          <div key={index} className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Fact 1</label>
              <p className="text-gray-700 dark:text-gray-300">{row.fact1}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Fact 2</label>
              <p className="text-gray-700 dark:text-gray-300">{row.fact2}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Punchline</label>
              <p className="text-gray-700 dark:text-gray-300">{row.punchline}</p>
            </div>
            {index !== 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => handleDeleteFactsRow(index)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                  title="Delete row"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Mobile New Row Form */}
        <div className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Fact 1</label>
            <textarea
              value={newFactsRow.fact1}
              onChange={(e) => setNewFactsRow({ ...newFactsRow, fact1: e.target.value })}
              placeholder="Enter your first fact..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Fact 2</label>
            <textarea
              value={newFactsRow.fact2}
              onChange={(e) => setNewFactsRow({ ...newFactsRow, fact2: e.target.value })}
              placeholder="Enter your second fact..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Punchline</label>
            <textarea
              value={newFactsRow.punchline}
              onChange={(e) => setNewFactsRow({ ...newFactsRow, punchline: e.target.value })}
              placeholder="Write your punchline..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={handleAddFactsRow}
          disabled={!newFactsRow.fact1 || !newFactsRow.fact2 || !newFactsRow.punchline}
          className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Row
        </button>
      </div>
    </div>
  );

  const renderExaggerationLadderTable = () => (
    <div className="space-y-6">
      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Mundane</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Slight</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[43%]">Absurd</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {exaggerationRows.map((row, index) => (
                  <tr key={index}>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.mundane}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.slight}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[43%]">{row.absurd}</td>
                    <td className="px-4 py-4 align-top w-16">
                      {index !== 0 && (
                        <button
                          onClick={() => handleDeleteExaggerationRow(index)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                          title="Delete row"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newExaggerationRow.mundane}
                      onChange={(e) => setNewExaggerationRow({ ...newExaggerationRow, mundane: e.target.value })}
                      placeholder="Enter your mundane thing..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newExaggerationRow.slight}
                      onChange={(e) => setNewExaggerationRow({ ...newExaggerationRow, slight: e.target.value })}
                      placeholder="Exaggerate it slightly..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[43%]">
                    <textarea
                      value={newExaggerationRow.absurd}
                      onChange={(e) => setNewExaggerationRow({ ...newExaggerationRow, absurd: e.target.value })}
                      placeholder="Exaggerate it absurdly..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="w-16"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="sm:hidden space-y-4">
        {exaggerationRows.map((row, index) => (
          <div key={index} className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Mundane</label>
              <p className="text-gray-700 dark:text-gray-300">{row.mundane}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Slight</label>
              <p className="text-gray-700 dark:text-gray-300">{row.slight}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Absurd</label>
              <p className="text-gray-700 dark:text-gray-300">{row.absurd}</p>
            </div>
            {index !== 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => handleDeleteExaggerationRow(index)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                  title="Delete row"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Mobile New Row Form */}
        <div className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Mundane</label>
            <textarea
              value={newExaggerationRow.mundane}
              onChange={(e) => setNewExaggerationRow({ ...newExaggerationRow, mundane: e.target.value })}
              placeholder="Enter your mundane thing..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Slight</label>
            <textarea
              value={newExaggerationRow.slight}
              onChange={(e) => setNewExaggerationRow({ ...newExaggerationRow, slight: e.target.value })}
              placeholder="Exaggerate it slightly..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Absurd</label>
            <textarea
              value={newExaggerationRow.absurd}
              onChange={(e) => setNewExaggerationRow({ ...newExaggerationRow, absurd: e.target.value })}
              placeholder="Exaggerate it absurdly..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={handleAddExaggerationRow}
          disabled={!newExaggerationRow.mundane || !newExaggerationRow.slight || !newExaggerationRow.absurd}
          className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Row
        </button>
      </div>
    </div>
  );

  const renderWhatIfTable = () => (
    <div className="space-y-6">
      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Scenario</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Twist</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[43%]">Outcome</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {whatIfRows.map((row, index) => (
                  <tr key={index}>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.scenario}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.twist}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[43%]">{row.outcome}</td>
                    <td className="px-4 py-4 align-top w-16">
                      {index !== 0 && (
                        <button
                          onClick={() => handleDeleteWhatIfRow(index)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                          title="Delete row"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newWhatIfRow.scenario}
                      onChange={(e) => setNewWhatIfRow({ ...newWhatIfRow, scenario: e.target.value })}
                      placeholder="Enter your scenario..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newWhatIfRow.twist}
                      onChange={(e) => setNewWhatIfRow({ ...newWhatIfRow, twist: e.target.value })}
                      placeholder="Add a twist..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[43%]">
                    <textarea
                      value={newWhatIfRow.outcome}
                      onChange={(e) => setNewWhatIfRow({ ...newWhatIfRow, outcome: e.target.value })}
                      placeholder="Write your outcome..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="w-16"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="sm:hidden space-y-4">
        {whatIfRows.map((row, index) => (
          <div key={index} className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Scenario</label>
              <p className="text-gray-700 dark:text-gray-300">{row.scenario}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Twist</label>
              <p className="text-gray-700 dark:text-gray-300">{row.twist}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Outcome</label>
              <p className="text-gray-700 dark:text-gray-300">{row.outcome}</p>
            </div>
            {index !== 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => handleDeleteWhatIfRow(index)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                  title="Delete row"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Mobile New Row Form */}
        <div className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Scenario</label>
            <textarea
              value={newWhatIfRow.scenario}
              onChange={(e) => setNewWhatIfRow({ ...newWhatIfRow, scenario: e.target.value })}
              placeholder="Enter your scenario..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Twist</label>
            <textarea
              value={newWhatIfRow.twist}
              onChange={(e) => setNewWhatIfRow({ ...newWhatIfRow, twist: e.target.value })}
              placeholder="Add a twist..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Outcome</label>
            <textarea
              value={newWhatIfRow.outcome}
              onChange={(e) => setNewWhatIfRow({ ...newWhatIfRow, outcome: e.target.value })}
              placeholder="Write your outcome..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={handleAddWhatIfRow}
          disabled={!newWhatIfRow.scenario || !newWhatIfRow.twist || !newWhatIfRow.outcome}
          className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Row
        </button>
      </div>
    </div>
  );

  const renderObjectComplaintTable = () => (
    <div className="space-y-6">
      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Object</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Complaint</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[43%]">Comeback</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {objectComplaintRows.map((row, index) => (
                  <tr key={index}>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.object}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.complaint}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[43%]">{row.comeback}</td>
                    <td className="px-4 py-4 align-top w-16">
                      {index !== 0 && (
                        <button
                          onClick={() => handleDeleteObjectComplaintRow(index)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                          title="Delete row"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newObjectComplaintRow.object}
                      onChange={(e) => setNewObjectComplaintRow({ ...newObjectComplaintRow, object: e.target.value })}
                      placeholder="Enter the object..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newObjectComplaintRow.complaint}
                      onChange={(e) => setNewObjectComplaintRow({ ...newObjectComplaintRow, complaint: e.target.value })}
                      placeholder="Enter the complaint..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[43%]">
                    <textarea
                      value={newObjectComplaintRow.comeback}
                      onChange={(e) => setNewObjectComplaintRow({ ...newObjectComplaintRow, comeback: e.target.value })}
                      placeholder="Enter your comeback..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="w-16"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="sm:hidden space-y-4">
        {objectComplaintRows.map((row, index) => (
          <div key={index} className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Object</label>
              <p className="text-gray-700 dark:text-gray-300">{row.object}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Complaint</label>
              <p className="text-gray-700 dark:text-gray-300">{row.complaint}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Comeback</label>
              <p className="text-gray-700 dark:text-gray-300">{row.comeback}</p>
            </div>
            {index !== 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => handleDeleteObjectComplaintRow(index)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                  title="Delete row"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Mobile New Row Form */}
        <div className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Object</label>
            <textarea
              value={newObjectComplaintRow.object}
              onChange={(e) => setNewObjectComplaintRow({ ...newObjectComplaintRow, object: e.target.value })}
              placeholder="Enter the object..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Complaint</label>
            <textarea
              value={newObjectComplaintRow.complaint}
              onChange={(e) => setNewObjectComplaintRow({ ...newObjectComplaintRow, complaint: e.target.value })}
              placeholder="Enter the complaint..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Comeback</label>
            <textarea
              value={newObjectComplaintRow.comeback}
              onChange={(e) => setNewObjectComplaintRow({ ...newObjectComplaintRow, comeback: e.target.value })}
              placeholder="Enter your comeback..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={handleAddObjectComplaintRow}
          disabled={!newObjectComplaintRow.object || !newObjectComplaintRow.complaint || !newObjectComplaintRow.comeback}
          className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Row
        </button>
      </div>
    </div>
  );

  const renderMishearingTable = () => (
    <div className="space-y-6">
      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Said</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Misheard</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[43%]">Punchline</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {mishearingRows.map((row, index) => (
                  <tr key={index}>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.said}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.misheard}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[43%]">{row.punchline}</td>
                    <td className="px-4 py-4 align-top w-16">
                      {index !== 0 && (
                        <button
                          onClick={() => handleDeleteMishearingRow(index)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                          title="Delete row"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newMishearingRow.said}
                      onChange={(e) => setNewMishearingRow({ ...newMishearingRow, said: e.target.value })}
                      placeholder="Enter what was said..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newMishearingRow.misheard}
                      onChange={(e) => setNewMishearingRow({ ...newMishearingRow, misheard: e.target.value })}
                      placeholder="Enter what was misheard..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[43%]">
                    <textarea
                      value={newMishearingRow.punchline}
                      onChange={(e) => setNewMishearingRow({ ...newMishearingRow, punchline: e.target.value })}
                      placeholder="Write your punchline..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="w-16"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="sm:hidden space-y-4">
        {mishearingRows.map((row, index) => (
          <div key={index} className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Said</label>
              <p className="text-gray-700 dark:text-gray-300">{row.said}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Misheard</label>
              <p className="text-gray-700 dark:text-gray-300">{row.misheard}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Punchline</label>
              <p className="text-gray-700 dark:text-gray-300">{row.punchline}</p>
            </div>
            {index !== 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => handleDeleteMishearingRow(index)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                  title="Delete row"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Mobile New Row Form */}
        <div className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Said</label>
            <textarea
              value={newMishearingRow.said}
              onChange={(e) => setNewMishearingRow({ ...newMishearingRow, said: e.target.value })}
              placeholder="Enter what was said..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Misheard</label>
            <textarea
              value={newMishearingRow.misheard}
              onChange={(e) => setNewMishearingRow({ ...newMishearingRow, misheard: e.target.value })}
              placeholder="Enter what was misheard..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Punchline</label>
            <textarea
              value={newMishearingRow.punchline}
              onChange={(e) => setNewMishearingRow({ ...newMishearingRow, punchline: e.target.value })}
              placeholder="Write your punchline..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={handleAddMishearingRow}
          disabled={!newMishearingRow.said || !newMishearingRow.misheard || !newMishearingRow.punchline}
          className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Row
        </button>
      </div>
    </div>
  );

  const renderPersonalFlawFlipTable = () => (
    <div className="space-y-6">
      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Flaw</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Superpower</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[43%]">Backfire</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {personalFlawFlipRows.map((row, index) => (
                  <tr key={index}>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.flaw}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.superpower}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[43%]">{row.backfire}</td>
                    <td className="px-4 py-4 align-top w-16">
                      {index !== 0 && (
                        <button
                          onClick={() => handleDeletePersonalFlawFlipRow(index)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                          title="Delete row"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newPersonalFlawFlipRow.flaw}
                      onChange={(e) => setNewPersonalFlawFlipRow({ ...newPersonalFlawFlipRow, flaw: e.target.value })}
                      placeholder="Enter your flaw..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newPersonalFlawFlipRow.superpower}
                      onChange={(e) => setNewPersonalFlawFlipRow({ ...newPersonalFlawFlipRow, superpower: e.target.value })}
                      placeholder="Enter your superpower..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[43%]">
                    <textarea
                      value={newPersonalFlawFlipRow.backfire}
                      onChange={(e) => setNewPersonalFlawFlipRow({ ...newPersonalFlawFlipRow, backfire: e.target.value })}
                      placeholder="Enter how it backfires..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="w-16"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="sm:hidden space-y-4">
        {personalFlawFlipRows.map((row, index) => (
          <div key={index} className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Flaw</label>
              <p className="text-gray-700 dark:text-gray-300">{row.flaw}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Superpower</label>
              <p className="text-gray-700 dark:text-gray-300">{row.superpower}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Backfire</label>
              <p className="text-gray-700 dark:text-gray-300">{row.backfire}</p>
            </div>
            {index !== 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => handleDeletePersonalFlawFlipRow(index)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                  title="Delete row"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Mobile New Row Form */}
        <div className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Flaw</label>
            <textarea
              value={newPersonalFlawFlipRow.flaw}
              onChange={(e) => setNewPersonalFlawFlipRow({ ...newPersonalFlawFlipRow, flaw: e.target.value })}
              placeholder="Enter your flaw..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Superpower</label>
            <textarea
              value={newPersonalFlawFlipRow.superpower}
              onChange={(e) => setNewPersonalFlawFlipRow({ ...newPersonalFlawFlipRow, superpower: e.target.value })}
              placeholder="Enter your superpower..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Backfire</label>
            <textarea
              value={newPersonalFlawFlipRow.backfire}
              onChange={(e) => setNewPersonalFlawFlipRow({ ...newPersonalFlawFlipRow, backfire: e.target.value })}
              placeholder="Enter how it backfires..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={handleAddPersonalFlawFlipRow}
          disabled={!newPersonalFlawFlipRow.flaw || !newPersonalFlawFlipRow.superpower || !newPersonalFlawFlipRow.backfire}
          className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Row
        </button>
      </div>
    </div>
  );

  const renderNicknameGeneratorTable = () => (
    <div className="space-y-6">
      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Action</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[28.5%]">Nickname</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 text-left text-orange-500 font-semibold whitespace-nowrap w-[43%]">Punchline</th>
                  <th className="border-b-2 border-orange-500 px-4 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {nicknameGeneratorRows.map((row, index) => (
                  <tr key={index}>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.action}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[28.5%]">{row.nickname}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 align-top w-[43%]">{row.punchline}</td>
                    <td className="px-4 py-4 align-top w-16">
                      {index !== 0 && (
                        <button
                          onClick={() => handleDeleteNicknameGeneratorRow(index)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                          title="Delete row"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newNicknameGeneratorRow.action}
                      onChange={(e) => setNewNicknameGeneratorRow({ ...newNicknameGeneratorRow, action: e.target.value })}
                      placeholder="Enter your action..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[28.5%]">
                    <textarea
                      value={newNicknameGeneratorRow.nickname}
                      onChange={(e) => setNewNicknameGeneratorRow({ ...newNicknameGeneratorRow, nickname: e.target.value })}
                      placeholder="Enter your nickname..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="px-4 py-4 w-[43%]">
                    <textarea
                      value={newNicknameGeneratorRow.punchline}
                      onChange={(e) => setNewNicknameGeneratorRow({ ...newNicknameGeneratorRow, punchline: e.target.value })}
                      placeholder="Write your punchline..."
                      className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
                      rows={4}
                    />
                  </td>
                  <td className="w-16"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="sm:hidden space-y-4">
        {nicknameGeneratorRows.map((row, index) => (
          <div key={index} className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Action</label>
              <p className="text-gray-700 dark:text-gray-300">{row.action}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Nickname</label>
              <p className="text-gray-700 dark:text-gray-300">{row.nickname}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-500">Punchline</label>
              <p className="text-gray-700 dark:text-gray-300">{row.punchline}</p>
            </div>
            {index !== 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => handleDeleteNicknameGeneratorRow(index)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                  title="Delete row"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Mobile New Row Form */}
        <div className="bg-white dark:bg-navy-800 rounded-lg shadow p-4 space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Action</label>
            <textarea
              value={newNicknameGeneratorRow.action}
              onChange={(e) => setNewNicknameGeneratorRow({ ...newNicknameGeneratorRow, action: e.target.value })}
              placeholder="Enter your action..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Nickname</label>
            <textarea
              value={newNicknameGeneratorRow.nickname}
              onChange={(e) => setNewNicknameGeneratorRow({ ...newNicknameGeneratorRow, nickname: e.target.value })}
              placeholder="Enter your nickname..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-orange-500">Punchline</label>
            <textarea
              value={newNicknameGeneratorRow.punchline}
              onChange={(e) => setNewNicknameGeneratorRow({ ...newNicknameGeneratorRow, punchline: e.target.value })}
              placeholder="Write your punchline..."
              className="w-full min-h-[100px] p-2 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y"
              rows={4}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={handleAddNicknameGeneratorRow}
          disabled={!newNicknameGeneratorRow.action || !newNicknameGeneratorRow.nickname || !newNicknameGeneratorRow.punchline}
          className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Row
        </button>
      </div>
    </div>
  );

  const renderWordAssociationTable = () => (
    <div className="space-y-6">
      {wordAssociationRows.length === 0 && (
        <button
          onClick={handleAddWordAssociationRow}
          className="w-full p-3 text-center border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-orange-500 hover:text-orange-500 transition-colors"
        >
          Get Random Words for Association
        </button>
      )}

      {wordAssociationRows.map((row, index) => (
        <div key={row.id} className="space-y-4 p-4 bg-white rounded-lg shadow">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Word 1</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={row.word1}
                  onChange={(e) => handleWordAssociationChange(row.id, 'word1', e.target.value)}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter first word"
                />
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Word 2</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={row.word2}
                  onChange={(e) => handleWordAssociationChange(row.id, 'word2', e.target.value)}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter second word"
                />
              </div>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  const [randomWord1, randomWord2] = getRandomNouns(2);
                  const newRows = wordAssociationRows.map((r) => {
                    if (r.id === row.id) {
                      return { 
                        ...r, 
                        word1: randomWord1, 
                        word2: randomWord2, 
                        associations1: [''], 
                        associations2: [''], 
                        finalJoke: '' 
                      };
                    }
                    return r;
                  });
                  setWordAssociationRows(newRows);
                }}
                className="p-2 text-orange-500 hover:text-orange-600 transition-colors"
                title="Get new random words"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

          {/* Associations */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* First word associations */}
            <div className="flex-1 space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Associations for "{row.word1 || 'First Word'}"
              </label>
              {row.associations1.map((assoc, assocIndex) => (
                <input
                  key={assocIndex}
                  type="text"
                  value={assoc}
                  onChange={(e) => {
                    const newAssociations = [...row.associations1];
                    newAssociations[assocIndex] = e.target.value;
                    handleWordAssociationChange(row.id, 'associations1', newAssociations);
                  }}
                  placeholder="Add association"
                  className="w-full p-2 border rounded"
                />
              ))}
              <button
                onClick={() => handleAddAssociation(row.id, 1)}
                className="text-sm text-orange-500 hover:text-orange-600"
              >
                + Add Association
              </button>
            </div>

            {/* Second word associations */}
            <div className="flex-1 space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Associations for "{row.word2 || 'Second Word'}"
              </label>
              {row.associations2.map((assoc, assocIndex) => (
                <input
                  key={assocIndex}
                  type="text"
                  value={assoc}
                  onChange={(e) => {
                    const newAssociations = [...row.associations2];
                    newAssociations[assocIndex] = e.target.value;
                    handleWordAssociationChange(row.id, 'associations2', newAssociations);
                  }}
                  placeholder="Add association"
                  className="w-full p-2 border rounded"
                />
              ))}
              <button
                onClick={() => handleAddAssociation(row.id, 2)}
                className="text-sm text-orange-500 hover:text-orange-600"
              >
                + Add Association
              </button>
            </div>
          </div>

          {/* Final joke */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Joke
            </label>
            <textarea
              value={row.finalJoke}
              onChange={(e) => handleWordAssociationChange(row.id, 'finalJoke', e.target.value)}
              placeholder="Write your joke using the connection you found..."
              className="w-full p-2 border rounded"
              rows={3}
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => handleDeleteWordAssociationRow(index)}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
              title="Delete row"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderRewritePunchlineTable = () => (
    <div className="space-y-6">
      {rewritePunchlineRows.length === 0 && (
        <button
          onClick={() => {
            const randomJoke = jokes[Math.floor(Math.random() * jokes.length)];
            const newRow: RewritePunchlineRow = {
              id: uuidv4(),
              comedian: randomJoke.Comedian,
              originalJoke: randomJoke.Setup,
              originalPunchline: randomJoke.Punchline,
              newPunchline: ''
            };
            setRewritePunchlineRows([...rewritePunchlineRows, newRow]);
          }}
          className="w-full p-3 text-center border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-orange-500 hover:text-orange-500 transition-colors"
        >
          Generate Random Joke
        </button>
      )}

      {rewritePunchlineRows.map((row, index) => (
        <div key={row.id} className="space-y-4 p-4 bg-white rounded-lg shadow">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Setup</label>
              <div className="relative">
                {row.comedian && (
                  <div className="absolute -top-6 right-0 text-sm text-gray-500">
                    by {row.comedian}
                  </div>
                )}
                <textarea
                  value={row.originalJoke}
                  onChange={(e) => handleRewritePunchlineChange(index, 'originalJoke', e.target.value)}
                  placeholder="Enter the setup..."
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Original Punchline</label>
              <textarea
                value={row.originalPunchline}
                onChange={(e) => handleRewritePunchlineChange(index, 'originalPunchline', e.target.value)}
                placeholder="Enter the original punchline..."
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows={3}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  const randomJoke = jokes[Math.floor(Math.random() * jokes.length)];
                  const updatedRows = rewritePunchlineRows.map((r) => {
                    if (r.id === row.id) {
                      return {
                        ...r,
                        comedian: randomJoke.Comedian,
                        originalJoke: randomJoke.Setup,
                        originalPunchline: randomJoke.Punchline,
                        newPunchline: ''
                      };
                    }
                    return r;
                  });
                  setRewritePunchlineRows(updatedRows);
                }}
                className="p-2 text-orange-500 hover:text-orange-600 transition-colors"
                title="Get new random joke"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your New Punchline</label>
            <div className="flex gap-2">
              <textarea
                value={row.newPunchline}
                onChange={(e) => handleRewritePunchlineChange(index, 'newPunchline', e.target.value)}
                placeholder="Write your new punchline..."
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows={3}
              />
              <button
                onClick={() => handleDeleteRewritePunchlineRow(index)}
                className="self-start p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                title="Delete row"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderReverseSetupTable = () => (
    <div className="space-y-6">
      {reverseSetupRows.length === 0 && (
        <button
          onClick={handleAddReverseSetupRow}
          className="w-full p-3 text-center border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-orange-500 hover:text-orange-500 transition-colors"
        >
          Get Random Punchline
        </button>
      )}

      {reverseSetupRows.map((row, index) => (
        <div key={row.id} className="space-y-4 p-4 bg-white dark:bg-navy-800 rounded-lg shadow">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Punchline</label>
              <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/50 rounded-lg text-gray-900 dark:text-white font-medium">
                {row.punchline}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Write Your Setup</label>
              <div className="flex gap-2">
                <textarea
                  value={row.setup}
                  onChange={(e) => {
                    const updatedRows = reverseSetupRows.map((r) =>
                      r.id === row.id ? { ...r, setup: e.target.value } : r
                    );
                    setReverseSetupRows(updatedRows);
                  }}
                  placeholder="Write a setup that naturally leads to this punchline..."
                  className="w-full p-2 border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={4}
                />
                <button
                  onClick={() => handleDeleteReverseSetupRow(index)}
                  className="self-start p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                  title="Delete row"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            <button
              onClick={handleAddReverseSetupRow}
              className="w-full p-2 border-2 border-dashed border-gray-300 dark:border-navy-600 text-gray-600 dark:text-gray-400 rounded hover:border-orange-500 hover:text-orange-500 transition-colors text-sm"
            >
              <Plus size={16} className="inline mr-1" /> Add Another Punchline
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderHeckleResponseTable = () => (
    <div className="space-y-6">
      {heckleResponseRows.length === 0 && (
        <button
          onClick={handleAddHeckleResponseRow}
          className="w-full p-3 text-center border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-orange-500 hover:text-orange-500 transition-colors"
        >
          Get Random Heckle
        </button>
      )}

      {heckleResponseRows.map((row, index) => (
        <div key={row.id} className="space-y-4 p-4 bg-white dark:bg-navy-800 rounded-lg shadow">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Heckle</label>
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg text-gray-900 dark:text-white font-medium italic">
                "{row.heckle}"
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Your Comeback</label>
              <div className="flex gap-2">
                <textarea
                  value={row.response}
                  onChange={(e) => {
                    const updatedRows = heckleResponseRows.map((r) =>
                      r.id === row.id ? { ...r, response: e.target.value } : r
                    );
                    setHeckleResponseRows(updatedRows);
                  }}
                  placeholder="Write a sharp, quick comeback that makes the audience laugh with you..."
                  className="w-full p-2 border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={3}
                />
                <button
                  onClick={() => handleDeleteHeckleResponseRow(index)}
                  className="self-start p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                  title="Delete row"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            <button
              onClick={handleAddHeckleResponseRow}
              className="w-full p-2 border-2 border-dashed border-gray-300 dark:border-navy-600 text-gray-600 dark:text-gray-400 rounded hover:border-orange-500 hover:text-orange-500 transition-colors text-sm"
            >
              <Plus size={16} className="inline mr-1" /> Get Another Heckle
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-0 md:p-4 space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-orange-500 mb-2">Comedy Writing Exercises</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Select an exercise from the dropdown below to start practicing and developing your comedy writing skills.
        </p>
      </div>

      {/* Exercise Selector Dropdown */}
      <div className="relative inline-block text-left w-full" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-left shadow-sm hover:border-orange-500 transition-colors flex items-center justify-between"
        >
          <span className="text-gray-700">
            {selectedExercise ? selectedExercise.title : 'Select an exercise'}
          </span>
          <ChevronDown
            className={`w-5 h-5 text-gray-500 transition-transform ${
              isDropdownOpen ? 'transform rotate-180' : ''
            }`}
          />
        </button>

        {isDropdownOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
            {INITIAL_EXERCISES.map((exercise) => (
              <button
                key={exercise.id}
                onClick={() => {
                  setSelectedExercise(exercise);
                  setIsDropdownOpen(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors"
              >
                <span className="text-gray-700">{exercise.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected Exercise Display */}
      {selectedExercise && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-orange-500">{selectedExercise.title}</h2>
            </div>

            <p className="text-gray-600">{selectedExercise.description}</p>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Instructions:</h3>
              <ol className="list-decimal list-inside space-y-2">
                {selectedExercise.instructions.map((instruction, index) => (
                  <li key={index} className="text-gray-600">{instruction}</li>
                ))}
              </ol>
            </div>

            {selectedExercise.id === 'ex1' && renderPremiseTable()}
            {selectedExercise.id === 'ex2' && renderFactsJokeTable()}
            {selectedExercise.id === 'ex3' && renderExaggerationLadderTable()}
            {selectedExercise.id === 'ex4' && renderWhatIfTable()}
            {selectedExercise.id === 'object-complaint' && renderObjectComplaintTable()}
            {selectedExercise.id === 'misheard-moment' && renderMishearingTable()}
            {selectedExercise.id === 'personal-flaw-flip' && renderPersonalFlawFlipTable()}
            {selectedExercise.id === 'nickname-generator' && renderNicknameGeneratorTable()}
            {selectedExercise.id === 'word-association' && renderWordAssociationTable()}
            {selectedExercise.id === 'rewrite-punchline' && renderRewritePunchlineTable()}
            {selectedExercise.id === 'reverse-setup' && renderReverseSetupTable()}
            {selectedExercise.id === 'heckle-responses' && renderHeckleResponseTable()}
          </div>
        </div>
      )}

      {/* My Daily Exercises — Calendar View */}
      <div className="mt-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            My Daily Exercises
            {!loadingSaves && allExercises.length > 0 && (
              <span className="ml-2 text-sm font-semibold text-gray-400">({allExercises.length} saved)</span>
            )}
          </h2>
          {allExercises.length > 0 && (
            <div className="relative w-full sm:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by word or content…"
                className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={14} />
                </button>
              )}
            </div>
          )}
        </div>

        {loadingSaves ? (
          <div className="text-center py-12 text-gray-400 text-sm">Loading…</div>
        ) : allExercises.length === 0 ? (
          <div className="bg-white dark:bg-navy-800 rounded-2xl border border-dashed border-gray-200 dark:border-navy-600 py-12 px-6 text-center">
            <p className="text-gray-500 dark:text-gray-400 font-medium">No saved exercises yet.</p>
            <p className="text-sm text-gray-400 mt-1">Complete a Daily Word exercise from the dashboard to see it here.</p>
          </div>
        ) : searchQuery ? (
          /* Search results — flat list */
          <div className="space-y-3">
            {searchResults && searchResults.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">No results for "{searchQuery}"</div>
            ) : (searchResults ?? []).map(exercise => (
              <div key={exercise.id} className="bg-white dark:bg-navy-800 rounded-2xl border border-gray-100 dark:border-navy-700 shadow-sm p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="font-black text-gray-900 dark:text-white capitalize">{exercise.word}</span>
                      <span className="text-xs text-gray-400">{exercise.date}</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{exercise.content}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(exercise.id)}
                    disabled={deletingId === exercise.id}
                    className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 mt-0.5"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Calendar view */
          <div className="bg-white dark:bg-navy-800 rounded-2xl border border-gray-100 dark:border-navy-700 shadow-sm overflow-hidden">
            {/* Month navigation */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-navy-700">
              <button
                onClick={handleLeftArrow}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-navy-700 text-gray-500 dark:text-gray-400 transition-colors"
                aria-label="Previous month"
              >
                <ChevronDown size={18} className="rotate-90" />
              </button>
              <span className="font-bold text-gray-900 dark:text-white">
                {MONTH_NAMES[calendarMonth - 1]} {calendarYear}
              </span>
              <button
                onClick={handleRightArrow}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-navy-700 text-gray-500 dark:text-gray-400 transition-colors"
                aria-label="Next month"
              >
                <ChevronDown size={18} className="-rotate-90" />
              </button>
            </div>

            {/* Day-of-week headers */}
            <div className="grid grid-cols-7 text-center px-3 pt-3">
              {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                <div key={d} className="text-xs font-semibold text-gray-400 dark:text-gray-500 pb-2">{d}</div>
              ))}
            </div>

            {/* Day cells */}
            <div className="grid grid-cols-7 text-center px-3 pb-4 gap-y-1">
              {/* Leading padding cells */}
              {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                <div key={`pad-${i}`} />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                const hasExercise = !!exerciseByDate[dateStr];
                const isSelected = selectedDate === dateStr;
                const isToday = dateStr === todayET;
                return (
                  <button
                    key={dateStr}
                    onClick={() => hasExercise && setSelectedDate(isSelected ? null : dateStr)}
                    disabled={!hasExercise}
                    className={[
                      'relative flex flex-col items-center justify-center rounded-xl py-1.5 text-sm font-medium transition-all',
                      hasExercise ? 'cursor-pointer' : 'cursor-default',
                      isSelected
                        ? 'bg-orange-500 text-white'
                        : hasExercise
                          ? 'hover:bg-orange-50 dark:hover:bg-orange-900/20 text-gray-900 dark:text-white'
                          : 'text-gray-300 dark:text-gray-600',
                    ].join(' ')}
                  >
                    <span className={isToday && !isSelected ? 'underline underline-offset-2 decoration-orange-400' : ''}>
                      {day}
                    </span>
                    {hasExercise && !isSelected && (
                      <span className="mt-0.5 w-1 h-1 rounded-full bg-orange-400 block" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected day — exercise content */}
            {selectedDate && exerciseByDate[selectedDate] && (
              <div className="border-t border-gray-100 dark:border-navy-700 px-5 py-5">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-baseline gap-3">
                    <span className="font-black text-lg text-gray-900 dark:text-white capitalize">
                      {exerciseByDate[selectedDate].word}
                    </span>
                    <span className="text-xs text-gray-400">{selectedDate}</span>
                  </div>
                  <button
                    onClick={() => handleDelete(exerciseByDate[selectedDate].id)}
                    disabled={deletingId === exerciseByDate[selectedDate].id}
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    <Trash2 size={13} />
                    {deletingId === exerciseByDate[selectedDate].id ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                  {exerciseByDate[selectedDate].content}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Exercises;
